import { Outlet } from 'react-router-dom';
import { PublicNav } from '@/components/PublicNav';
import { LanguageProvider } from '@/i18n/LanguageContext';
import { useLang } from '@/i18n/LanguageContext';
import { ui } from '@/i18n/content/ui';
import { NewsletterForm } from '@/components/NewsletterForm';
import { ThemeProvider, useTheme } from '@/contexts/ThemeContext';
import type { Lang } from '@/i18n/routes';

export function PublicLayout({ lang = 'nl' }: { lang?: Lang }) {
  return (
    <ThemeProvider>
      <LanguageProvider lang={lang}>
        <PublicShell />
      </LanguageProvider>
    </ThemeProvider>
  );
}

function PublicShell() {
  const { isDark } = useTheme();
  return (
    <div
      className={`${isDark ? 'dark ' : ''}min-h-screen bg-sitebg text-sitefg flex flex-col`}
    >
      <PublicNav />
      <div className="flex-1">
        <Outlet />
      </div>
      <PublicFooter />
    </div>
  );
}


function PublicFooter() {
  const lang = useLang();
  const labels = ui[lang];
  return (
    <footer className="border-t border-sitefg/10 bg-sitebg">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 gap-8 pb-8 border-b border-sitefg/10">
          <div>
            <h2 className="text-sm font-medium tracking-wide text-sitefg/80 mb-2">
              {labels.newsletterFooterTitle}
            </h2>
            <p className="text-sm text-sitefg/55 mb-4">{labels.newsletterFooterText}</p>
            <NewsletterForm source="footer" inline />
          </div>
          <div className="md:text-right">
            <p className="text-xs font-mono tracking-wider text-sitefg/40">
              Perpetual Plexus · Rot8ion · {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

interface ContentPageProps {
  title: string;
  intro?: string;
  children: React.ReactNode;
}

/** Shared shell for the text pages (Statement, Recent, CV, Media, Bio, Contact, Newsletter). */
export function ContentPage({ title, intro, children }: ContentPageProps) {
  return (
    <div>
      <header className="max-w-3xl mx-auto px-6 pt-16 pb-8">
        <h1 className="text-3xl md:text-5xl font-light tracking-tight">{title}</h1>
        {intro && (
          <p className="mt-4 text-base leading-relaxed text-sitefg/60">{intro}</p>
        )}
      </header>
      <main className="max-w-3xl mx-auto px-6 pb-24">{children}</main>
    </div>
  );
}

/** Body text paragraph with consistent rhythm. */
export function P({ children }: { children: React.ReactNode }) {
  return <p className="mb-5 leading-relaxed text-sitefg/75">{children}</p>;
}
